import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, PlayCircle } from "lucide-react";
import { useI18n } from "../../i18n";
import { api, apiError } from "../../lib/api";
import Navbar from "../../components/marketing/Navbar";
import Footer from "../../components/marketing/Footer";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const TXNS = ["domestic", "exports", "imports", "gcc_transactions", "non_resident_services", "reverse_charge", "exempt_supplies", "government_contracts", "real_estate", "capital_assets", "credit_notes", "intercompany", "related_party"];
const COMPLEXITY_COLOR = { low: "hsl(152 76% 32%)", moderate: "hsl(38 92% 45%)", high: "hsl(24 90% 50%)", complex: "hsl(354 78% 54%)" };
const COMPLEXITY_LABEL = { low: { en: "Low", ar: "منخفض" }, moderate: { en: "Moderate", ar: "متوسط" }, high: { en: "High", ar: "مرتفع" }, complex: { en: "Complex", ar: "معقّد" } };

export default function Diagnostic() {
  const { t, lang, dir } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ company_name: "", email: "", sector: "", sector_type: "private", entities: 1, transactions: [] });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acts, setActs] = useState({});
  const Arrow = ArrowRight;
  useEffect(() => { api.get("/sector-activities").then((r) => setActs(r.data)).catch(() => {}); }, []);
  const sampleJourney = async () => {
    try { await api.post("/auth/demo/management"); const { data } = await api.get("/sample-case"); window.location.href = `/app/cases/${data.id}`; }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleTxn = (x) => setForm((f) => ({ ...f, transactions: f.transactions.includes(x) ? f.transactions.filter((v) => v !== x) : [...f.transactions, x] }));

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/diagnostic", form);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 pt-14 pb-24">
        {!result ? (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[hsl(217_63%_15%)]">{t("diag_title")}</h1>
            <p className="mt-3 text-lg text-muted-foreground">{t("diag_sub")}</p>
            <div className="mt-10 rounded-2xl border border-border bg-card p-7 gc-ring-shadow space-y-6">
              <div className="grid sm:grid-cols-2 gap-5">
                <div><Label>{t("diag_company")}</Label><Input data-testid="diag-company" className="mt-1.5" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></div>
                <div><Label>{t("diag_email")}</Label><Input data-testid="diag-email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div>
                  <Label>{t("sector_activity")}</Label>
                  <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                    <SelectTrigger data-testid="diag-sector" className="mt-1.5"><SelectValue placeholder={t("select_ph")} /></SelectTrigger>
                    <SelectContent className="max-h-60">{(acts[form.sector_type] || []).map((a) => <SelectItem key={a.en} value={a.en}>{lang === "ar" ? a.ar : a.en}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">{t("sector_type")}</Label>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {["government", "private", "nonprofit"].map((s) => (
                      <button key={s} type="button" data-testid={`diag-sector-type-${s}`} onClick={() => setForm({ ...form, sector_type: s, sector: "" })}
                        className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${form.sector_type === s ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "border-border hover:border-[hsl(158_64%_28%)]"}`}>
                        {t(`sec_${s}`)}
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label>{t("diag_entities")}</Label><Input data-testid="diag-entities" type="number" min={1} className="mt-1.5" value={form.entities} onChange={(e) => setForm({ ...form, entities: parseInt(e.target.value || "1") })} /></div>
              </div>
              <div>
                <Label className="mb-3 block">{t("diag_txn_q")}</Label>
                <div className="flex flex-wrap gap-2">
                  {TXNS.map((x) => (
                    <button key={x} data-testid={`diag-txn-${x}`} onClick={() => toggleTxn(x)}
                      className={`text-sm rounded-full px-3.5 py-1.5 border transition-colors ${form.transactions.includes(x) ? "bg-[hsl(217_63%_15%)] text-white border-transparent" : "border-border hover:border-[hsl(158_64%_28%)]"}`}>
                      {t(`txn_${x}`)}
                    </button>
                  ))}
                </div>
              </div>
              <Button data-testid="diag-submit-btn" disabled={loading} onClick={submit} className="w-full rounded-full h-12 bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">
                {loading ? t("loading") : t("diag_run")}
              </Button>
            </div>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-3xl font-bold text-[hsl(217_63%_15%)]">{t("diag_result")}</h1>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="text-xs text-muted-foreground">{t("diag_complexity")}</div>
                <div className="mt-2 font-display text-2xl font-bold" style={{ color: COMPLEXITY_COLOR[result.complexity] }}>{COMPLEXITY_LABEL[result.complexity][lang]}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="text-xs text-muted-foreground">{t("diag_areas")}</div>
                <div className="mt-2 font-mono text-3xl font-bold text-[hsl(24_90%_50%)]">{result.areas_count}</div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <div className="text-xs text-muted-foreground">{t("diag_recommended")}</div>
                <div className="mt-2 font-display text-2xl font-bold text-[hsl(158_64%_28%)]">{lang === "ar" ? result.recommended_plan.name_ar : result.recommended_plan.name_en}</div>
              </div>
            </div>
            {result.risk_areas.length > 0 && (
              <div className="mt-6 rounded-2xl border border-border bg-card p-6">
                <div className="font-semibold text-[hsl(217_63%_15%)] mb-4">{t("diag_areas")}</div>
                <div className="space-y-2">
                  {result.risk_areas.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <AlertTriangle className="h-4 w-4 text-[hsl(38_92%_45%)]" /><span>{a[lang]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-6 rounded-xl bg-secondary/60 border border-border p-4 text-xs text-muted-foreground">{t("diag_disclaimer")}</div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="diag-start-sub-btn" onClick={() => nav("/register")} className="rounded-full h-12 px-8 bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">
                {t("cta_start")} <Arrow className="ms-2 h-4 w-4" style={{ transform: dir === "rtl" ? "scaleX(-1)" : "none" }} />
              </Button>
              <Button data-testid="sample-journey-btn" onClick={sampleJourney} variant="outline" className="rounded-full h-12 px-8 border-[hsl(158_64%_28%)] text-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_97%)]">
                <PlayCircle className="me-2 h-4 w-4" /> {t("view_sample_journey")}
              </Button>
            </div>
          </motion.div>
        )}
      </section>
      <Footer />
    </div>
  );
}
